import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import {
  useGuestGroups,
  useGuestGroup,
  useCreateGroup,
  useDeleteGroup,
  useAddGroupMembers,
  useRemoveGroupMember,
} from '../hooks/useGuestGroups';

// ─── Create Group Dialog ─────────────────────────────────────────────────────

function CreateGroupDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const createGroup = useCreateGroup();

  function handleCreate() {
    createGroup.mutate(name, {
      onSuccess: () => {
        setName('');
        onClose();
      },
    });
  }

  function handleClose() {
    setName('');
    createGroup.reset();
    onClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Create Guest Group</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label="Group Name"
          placeholder="e.g. Family, Work Friends"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          sx={{ mt: 1 }}
        />
        {createGroup.isError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {(createGroup.error as Error).message || 'Failed to create group'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!name.trim() || createGroup.isPending}
        >
          {createGroup.isPending ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Add Members Dialog ──────────────────────────────────────────────────────

function AddMembersDialog({
  open,
  onClose,
  groupId,
}: {
  open: boolean;
  onClose: () => void;
  groupId: string;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const addMembers = useAddGroupMembers();

  function handleAdd() {
    addMembers.mutate(
      {
        groupId,
        members: [{ email: email.trim().toLowerCase(), name: name.trim(), notes: notes.trim() || undefined }],
      },
      {
        onSuccess: () => {
          setEmail('');
          setName('');
          setNotes('');
        },
      },
    );
  }

  function handleClose() {
    setEmail('');
    setName('');
    setNotes('');
    addMembers.reset();
    onClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add Member</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />
          <TextField
            label="Notes (optional)"
            placeholder="Dietary restrictions, etc."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          {addMembers.isError && (
            <Alert severity="error">
              {(addMembers.error as Error).message || 'Failed to add member'}
            </Alert>
          )}
          {addMembers.isSuccess && addMembers.data.created.length > 0 && (
            <Alert severity="success">Member added successfully</Alert>
          )}
          {addMembers.isSuccess && addMembers.data.skipped.length > 0 && (
            <Alert severity="warning">Email already exists in this group</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={!email.trim() || !name.trim() || addMembers.isPending}
        >
          {addMembers.isPending ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Group Detail View ───────────────────────────────────────────────────────

function GroupDetail({
  groupId,
  onBack,
}: {
  groupId: string;
  onBack: () => void;
}) {
  const { data: group, isLoading, isError } = useGuestGroup(groupId);
  const removeMember = useRemoveGroupMember();
  const deleteGroup = useDeleteGroup();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  function handleDeleteGroup() {
    deleteGroup.mutate(groupId, {
      onSuccess: () => onBack(),
    });
  }

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="text" width="40%" height={40} />
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={60} />
        ))}
      </Stack>
    );
  }

  if (isError || !group) {
    return <Alert severity="error">Failed to load group</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <IconButton onClick={onBack} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>
          {group.name}
        </Typography>
        <Button
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setConfirmDelete(true)}
          size="small"
        >
          Delete Group
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={3}>
        {group.members.length} member{group.members.length !== 1 ? 's' : ''}
      </Typography>

      <Button
        variant="outlined"
        startIcon={<PersonAddIcon />}
        onClick={() => setAddDialogOpen(true)}
        sx={{ mb: 2 }}
      >
        Add Member
      </Button>

      {group.members.length === 0 && (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
          No members yet. Add some members to this group.
        </Typography>
      )}

      <Stack spacing={1}>
        {group.members.map((member) => (
          <Card key={member.id} variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle2" noWrap>
                    {member.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {member.email}
                  </Typography>
                  {member.notes && (
                    <Typography variant="caption" color="text.secondary">
                      {member.notes}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() =>
                    removeMember.mutate({ groupId, memberId: member.id })
                  }
                  disabled={removeMember.isPending}
                >
                  <PersonRemoveIcon fontSize="small" />
                </IconButton>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <AddMembersDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        groupId={groupId}
      />

      {/* Confirm delete dialog */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete Group?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{group.name}&quot; and all its members?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteGroup}
            disabled={deleteGroup.isPending}
          >
            {deleteGroup.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function GuestGroupsPage() {
  const { data: groups, isLoading, isError } = useGuestGroups();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  // Show detail view if a group is selected
  if (selectedGroupId) {
    return (
      <Box>
        <GroupDetail
          groupId={selectedGroupId}
          onBack={() => setSelectedGroupId(null)}
        />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        mb={4}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Guest Groups
          </Typography>
          <Typography variant="body1" color="text.secondary" mt={0.5}>
            Create reusable guest lists to quickly add to events
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{ whiteSpace: 'nowrap' }}
        >
          New Group
        </Button>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load guest groups. Please try again.
        </Alert>
      )}

      {isLoading && (
        <Stack spacing={2}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={72} />
          ))}
        </Stack>
      )}

      {!isLoading && !isError && groups && groups.length === 0 && (
        <Box textAlign="center" py={8}>
          <GroupIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No guest groups yet
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            Groups let you save lists of people and quickly import them into events.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Your First Group
          </Button>
        </Box>
      )}

      {!isLoading && groups && groups.length > 0 && (
        <Stack spacing={2}>
          {groups.map((group) => (
            <Card
              key={group.id}
              sx={{
                cursor: 'pointer',
                transition: 'box-shadow 0.2s, transform 0.2s',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-1px)',
                },
              }}
              onClick={() => setSelectedGroupId(group.id)}
            >
              <CardContent>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <GroupIcon color="primary" />
                    <Box>
                      <Typography variant="h6">{group.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Created {new Date(group.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Stack>
                  <Chip
                    label={`${group.memberCount} member${group.memberCount !== 1 ? 's' : ''}`}
                    variant="outlined"
                  />
                </Stack>
              </CardContent>
              <Divider />
            </Card>
          ))}
        </Stack>
      )}

      <CreateGroupDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </Box>
  );
}
